import './index.css'

import React, {useContext, useEffect, useState} from "react";
import SettingModal from "./component/SettingModal.tsx";
import PlayTipModal from "./component/PlayTipsModal.tsx";
import ProfilePage from "./component/profile/profile.tsx";
import Leaderboard from "../leaderboard";
import {useMusicContext} from "../../providers/musicProvider/music-player-context.tsx";
import {ThemeContext} from "../../providers/themeContext/theme-context.tsx";
import DownloadDialog from "./component/DownloadDialog/DownloadDialog.tsx";
import {PageEnum} from "../../types/IPage.ts";
import {useUserContext} from "../../providers/userProvider/user-context.tsx";
import {JudgmentPlatform} from "../../common/utils/checkPlatform.ts";
import {GAEvents} from "../../common/utils/GA4/GAEvents.ts";
import {LocalStorageKey} from "../../types/IGameStat.ts";
import MedalDialog from "./component/MedalDialog/MedalDialog.tsx";
import {apiService} from "../../common/utils/http/api-service.ts";
import Countdown from "../../components/CountDown/CountDown.tsx";
import Avatar from "../../components/Avatar/Avatar.tsx";
import {IMedal, IUserGame, IVSPlayerRoomInfo} from "../../common/utils/http/IResponse.ts";
import debounce from "lodash/debounce";
import JoinMatchDialog from "../vsPlayer/components/JoinMatchDialog/JoinMatchDialog.tsx";
import {UrlHandler} from "../../common/utils/urlHandler/UrlHandler.ts";
import {CommonKey} from "../../common/common-key/CommonKey.ts";
import LoginModal from "./component/LoginModal/LoginModal.tsx";

interface Props {
    switchPage: (page: PageEnum) => void,
    setVsPlayerInfo: (value: IVSPlayerRoomInfo) => void
}

// home页面
const HomePage: React.FC<Props> = ({switchPage, setVsPlayerInfo}) => {
    // 全局用户信息provider
    const {nextTime, setNextTime} = useUserContext();
    // 是否显示 Join 弹窗
    const [showJoinModal, setShowJoinModal] = useState<boolean>(false);
    // 全局音乐provider
    const musicManager = useMusicContext();
    // 主题全局管理器
    const {imgUrls} = useContext(ThemeContext);
    // 当前日期
    const [currentDate, setCurrentDate] = useState<string>('')
    // 是否显示下载弹窗
    const [showDownloadDialog, setShowDownloadDialog] = useState<boolean>(false)
    // 是否弹出设置弹窗
    const [showSetting, setShowSetting] = useState<boolean>(false)
    // 是否显示游戏提示
    const [showPlayTip, setShowPlayTip] = useState<boolean>(false)
    // 是否显示 profile弹窗
    const [showProfile, setShowProfile] = useState<boolean>(false)
    // 是否显示勋章弹窗
    const [showMedalDialog, setShowMedalDialog] = useState<boolean>(false)
    // 是否显示登录弹窗
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false)
    // 是否显示排行榜
    const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false)
    // 判断当前是否是在手机浏览器登录
    const isMobileBrowser = JudgmentPlatform.isMobileBrowser()
    // 已经装备的徽章列表
    const [equippedMedalList, setEquippedMedalList] = useState<IMedal[]>([])
    // 游戏数据
    const [gameData, setGameData] = useState<IUserGame>();

    useEffect(() => {
        // 判断是否要显示下载弹窗
        hasShowDownloadDialog();
        // 发送GA埋点
        GAEvents.homePageShow();
        musicManager.playMusic()
        // 获取当前日期，用于显示在底部
        getCurrentDate();
        // 加载每日游戏的nextTime
        loadNextDailyGameTime()
        // 进入页面判断地址是否是有roomId
        checkRoomIdInUrl();
    }, []);

    // 检测网址中是否存在roomId，如果有，就弹出Battle Link弹窗
    const checkRoomIdInUrl = () => {
        const roomIdFromUrl = UrlHandler.getCurrentUrlParam(CommonKey.ROOM_ID)
        if (roomIdFromUrl) {
            setShowJoinModal(true)
        }
    }


    // 加载已经装备的徽章列表
    const loadEquippedMedalList = debounce(async () => {
        const response = await apiService.loadEquippedMedals();
        if (response.code === 200) {
            setEquippedMedalList(response.data)
        }
    }, 100)

    // 加载每日游戏的nextTime
    const loadNextDailyGameTime = async () => {
        const response = await apiService.getDailyGameNextTime();
        if (response.code === 200) {
            setNextTime(response.data ?? 0)
        }
    }

    // 判断是否有显示过下载弹窗
    const hasShowDownloadDialog = () => {
        const homeDownloadStatus = localStorage.getItem(LocalStorageKey.HOME_APP_DOWNLOAD_DIALOG);
        if (homeDownloadStatus) {
            if (homeDownloadStatus === "noShowed") {
                const isInAppWeb = JudgmentPlatform.isMobileBrowser()
                if (isInAppWeb) {
                    setShowDownloadDialog(true)
                    // 如果不在app内，就弹出下载弹窗，然后保存标记到本地
                    localStorage.setItem(LocalStorageKey.HOME_APP_DOWNLOAD_DIALOG, "hasShowed")
                }
            }
        }
    }

    // 播放按钮提示音
    const playBtnAudio = () => {
        musicManager.playButtonSound();
    }

    // 显示设置弹窗
    const showSettingModal = () => {
        // 发送GA埋点
        GAEvents.homePageSettingButtonClick()
        // 播放按键声
        playBtnAudio();
        setShowSetting(true);
    }

    // 显示游戏排行榜
    const showLeaderboardModal = () => {
        // 发送GA埋点
        GAEvents.homePageGameModeClick('Leaderboard')
        // 播放按键声
        playBtnAudio();
        setShowLeaderboard(true);
    }

    // 显示profile弹窗
    const showProfileModal = () => {
        // 发送GA埋点
        GAEvents.homePageProfileClick()
        // 播放按键声
        playBtnAudio();
        setShowProfile(true);
    }

    // 计算当前日期并格式化
    const getCurrentDate = () => {
        // 获取当前日期并格式化
        const date = new Date();
        const options: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long', day: '2-digit'};

        const formattedDate = date.toLocaleDateString('en-US', options);
        setCurrentDate(formattedDate);
    };

    // 显示游戏提示的方法
    const showPlayTipModal = () => {
        // GA埋点
        GAEvents.settingButtonClick('How to Play')
        // 播放按键声
        playBtnAudio();
        // 关闭设置弹窗
        setShowSetting(false)
        // 显示游戏提示
        setShowPlayTip(true)
    }

    // 显示下载弹窗
    const showDownloadModal = () => {
        // 发送GA埋点
        GAEvents.homePageDownloadButtonClick()
        // 播放按键声
        playBtnAudio();
        // 关闭设置弹窗
        setShowDownloadDialog(true)
    }

    // 跳转daily游戏界面
    const goToDailyGamePage = () => {
        // 发送GA埋点
        GAEvents.homePageGameModeClick('Daily')
        // 播放按键声
        playBtnAudio();
        // 切换页面
        switchPage(PageEnum.DAILY_GAME)
    }

    // 跳转到challenge页面
    const goToChallengePage = () => {
        // 发送GA埋点
        GAEvents.homePageGameModeClick('Challenge')
        // 播放按键声
        playBtnAudio();
        // 切换页面
        switchPage(PageEnum.CHALLENGE)
    }

    // 跳转vs-player游戏界面
    const goToVsPlayerHomePage = () => {
        // 发送GA埋点
        GAEvents.homePageGameModeClick('VS Player')
        // 播放按键声
        playBtnAudio();
        // 切换页面
        switchPage(PageEnum.VS_PLAYER_HOME)
    }

    // 跳转 UGC 主页面
    const goToUGCPage = () => {
        // 发送GA埋点
        GAEvents.homePageGameModeClick('Word Maker')
        // 播放按键声
        playBtnAudio();
        // 切换页面
        switchPage(PageEnum.WORD_MAKER_HOME)
    }

    // 设置gameData
    const handleGameData = (data: IUserGame) => {
        setGameData(data)
    }

    return (
        <div className="home-page">
            <img className="common-embellish-bg-left-top-page" src={imgUrls.leftTopBgUrl} alt="pic"/>
            <img className="common-embellish-bg-right-bottom-page" src={imgUrls.rightBottomBgUrl} alt="pic"/>

            {/*<style>{`body { background-color: #F1F4FF }`}</style>*/}
            <div className="display-flex-column">
                {/*顶部标题栏*/}
                <div className="top-title-container" style={{backgroundImage: `url(${imgUrls.titleBarBgUrl})`}}>
                    <Avatar className="width-40 height-40 margin-left-11" onClick={showProfileModal}/>
                    {/*<img onClick={() => showProfileModal()} className="avatar"*/}
                    {/*     src={user?.cgtnAvatar ?? imgUrls.defaultAvatarUrl} alt="avatar"/>*/}
                    {/*只有手机浏览器上才显示*/}
                    {isMobileBrowser ?
                        <img onClick={showDownloadModal} className="download-button" src={imgUrls.downloadIconUrl}
                             alt='pic'/> : undefined}
                    <img onClick={showSettingModal}
                         className={`setting-button ${isMobileBrowser ? 'margin-left-8' : 'margin-left-auto'}`}
                         src={imgUrls.settingIconUrl} alt="pic"/>

                </div>
                {/*logo*/}
                <img className="home-logo-container" src={imgUrls.logoImgUrl} alt="pic"/>
                {/*标题文字*/}
                <img className="home-name-text-container" src={imgUrls.logoTextImgUrl} alt="pic"/>
                {/*daily卡片*/}
                <div onClick={goToDailyGamePage} className="daily-card">
                    <img className="icon"
                         src={imgUrls.dailyCalendarIconUrl}
                         alt="pic"/>
                    <div className="daily-content-container">
                        <div className="my-roboto-bold daily-title">Daily</div>
                        <div className="my-roboto-regular daily-content">One word a day
                        </div>
                        <div className="my-roboto-regular daily-content">One world to play
                        </div>
                        {nextTime > 0 && <div
                            className="my-roboto-regular margin-top-4 margin-left-14 color-white display-flex-row display-flex-align-items-center font-size-14">
                            <div>Next daily puzzle in:</div>
                            <Countdown className="my-roboto-medium font-size-14 margin-left-4"
                                       startCount={nextTime}
                                       interval={1000}
                                       showHours={true}
                                       onTick={(num) => {
                                           if (num <= 0) {
                                               setNextTime(num)
                                           }
                                       }}/>
                        </div>}
                    </div>
                </div>

                <div className="second-container">
                    {/*challenge卡片*/}
                    <div onClick={goToChallengePage} className="challenge-card">
                        <img className="challenge-icon"
                             src={imgUrls.challengeIconUrl}
                             alt="pic"/>
                        <div className="">
                            <div className="challenge-title my-roboto-bold">Challenge</div>
                            <div className="challenge-content my-roboto-regular">Climb the levels.</div>
                        </div>
                    </div>
                    {/*leaderboard卡片*/}
                    <div className="leaderboard-card" onClick={showLeaderboardModal}>
                        <img className="home-leader-board-icon"
                             src={imgUrls.leaderboardIconUrl} alt="pic"/>
                        <div className="leaderboard-title my-roboto-bold">Leaderboards</div>
                    </div>
                </div>
                <div onClick={goToVsPlayerHomePage} className="vs-player-card">
                    <img className="icon"
                         src={imgUrls.vsPlayerIconUrl}
                         alt="pic"/>
                    <div className="vs-player-content-container">
                        <div className="my-roboto-bold margin-left-14 font-size-20 color-white margin-bottom-4">
                            vs Player
                        </div>
                        <div
                            className="my-roboto-regular margin-left-14 font-size-16 color-white my-roboto-regular">
                            Play with your friends.
                        </div>
                    </div>
                </div>
                <div onClick={goToUGCPage} className="word-maker-card">
                    <img className="icon"
                         src={imgUrls.wordMakerIconUrl}
                         alt="pic"/>
                    <div className="word-maker-content-container">
                        <div className="my-roboto-bold margin-left-14 font-size-20 color-white margin-bottom-4">Word
                            Maker
                        </div>
                        <div
                            className="my-roboto-regular margin-left-14 font-size-14 color-white my-roboto-regular">
                            Join the Word Maker community to play customized puzzles.
                        </div>
                    </div>
                </div>
                {/*底部当前日期*/}
                <div className="margin-top-10 text-center font-size-16 my-roboto-medium color-grey">
                    {currentDate}
                </div>
                {/*设置弹窗*/}
                <SettingModal key="homeSettingModal" isOpen={showSetting} onClose={() => setShowSetting(false)}
                              onShowPlayTip={showPlayTipModal}/>
                {/*app下载弹窗*/}
                <DownloadDialog key="downloadModal" isOpen={showDownloadDialog}
                                onClose={() => setShowDownloadDialog(false)}/>
                {/*游戏提示弹窗*/}
                <PlayTipModal key="playTipModal" isOpen={showPlayTip} onClose={() => setShowPlayTip(false)}/>
                {/*个人profile的弹窗*/}
                <ProfilePage key="profilePage"
                             onOpenLoginModal={()=> setShowLoginModal(true)}
                             gameData={gameData}
                             setGameData={handleGameData}
                             equippedMedalList={equippedMedalList}
                             loadEquippedMedalList={loadEquippedMedalList}
                             isOpen={showProfile} onClose={() => setShowProfile(false)}
                             switchPage={switchPage} onShowMedalDialog={() => setShowMedalDialog(true)}/>
                {/*勋章弹窗*/}
                <MedalDialog key="medalDialog"
                             gameData={gameData}
                             equippedMedalList={equippedMedalList}
                             loadEquippedMedalList={loadEquippedMedalList}
                             isOpen={showMedalDialog} onClose={() => setShowMedalDialog(false)}/>
                {/*排行榜弹窗*/}
                <Leaderboard key="leaderboardPage" isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)}/>
                {/*游戏房间链接弹窗*/}
                <JoinMatchDialog isOpen={showJoinModal}
                                 onClose={() => setShowJoinModal(false)}
                                 switchPage={switchPage} setVsPlayerInfo={setVsPlayerInfo}/>
                <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)}  switchPage={switchPage}/>
            </div>
        </div>
    );
}

export default HomePage;